import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import * as serviceActions from '../../actions/service';
import { axiosCaller } from '../../utils/apiCaller';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import { SERVICING_STATUS_COLORS, SERVICING_PRIORITY_COLORS } from '../../constants/constants';
import { toast } from 'react-toastify';
import '../Contact/Contact.scss';
import '../Transport/Transport.scss';
import '../Installation/Installation.scss';
import MentionInput from '../../components/MentionInput/MentionInput';
import MentionText from '../../components/MentionInput/MentionText';

const fmtDate = (d: string | null): string => {
    if (!d) return '-';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return String(dt.getDate()).padStart(2,'0') + '/' + String(dt.getMonth()+1).padStart(2,'0') + '/' + dt.getFullYear() + ' ' + String(dt.getHours()).padStart(2,'0') + ':' + String(dt.getMinutes()).padStart(2,'0');
};

const fmtDateOnly = (d: string | null): string => {
    if (!d) return '-';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return String(dt.getDate()).padStart(2,'0') + '/' + String(dt.getMonth()+1).padStart(2,'0') + '/' + dt.getFullYear();
};

const fmtDtShort = (d: string | null): string => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return String(dt.getDate()).padStart(2,'0') + '/' + String(dt.getMonth()+1).padStart(2,'0') + ' ' + String(dt.getHours()).padStart(2,'0') + ':' + String(dt.getMinutes()).padStart(2,'0');
};

var parseCoords = function(loc: string | null) {
    if (!loc) return null;
    var parts = loc.split(',').map(function(s) { return parseFloat(s.trim()); });
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) return parts;
    return null;
};

const eventColors: Record<string, string> = {
    created: '#3b82f6', status_changed: '#8b5cf6', file_uploaded: '#64748b', file_deleted: '#94a3b8',
};

var ALLOWED_TRANSITIONS: Record<number, number[]> = {
    1: [2, 6], 2: [3, 4, 5, 6], 3: [2, 4, 6], 4: [3, 5, 6], 5: [2, 4, 6], 6: [],
};

var TRANSITION_I18N: Record<number, { bg: string; id: string; defaultMessage: string }> = {
    2: { bg: '#3b82f6', id: 'service.detail.transition.startDiagnosis', defaultMessage: 'Fillo diagnostikimin' },
    3: { bg: '#f59e0b', id: 'service.detail.transition.waitMaterial',   defaultMessage: 'Prit materialin' },
    4: { bg: '#8b5cf6', id: 'service.detail.transition.startRepair',    defaultMessage: 'Fillo riparimin' },
    5: { bg: '#f97316', id: 'service.detail.transition.waitClient',     defaultMessage: 'Prit klientin' },
    6: { bg: '#22c55e', id: 'service.detail.transition.finish',         defaultMessage: 'Përfundo' },
};

interface Props {
    currentServicing: any;
    permissions: any;
    loading: boolean;
    actions: any;
    history: any;
    match: any;
}

const ServicingDetail: React.FC<Props> = (props) => {
    var intl = useIntl();
    var fm = function(id: string, defaultMessage: string, values?: Record<string, any>) {
        return intl.formatMessage({ id: id, defaultMessage: defaultMessage }, values);
    };

    var formatActiveSeconds = function(seconds: number): string {
        if (seconds <= 0) return fm('installation.detail.duration.zeroMin', '0 min');
        var days = Math.floor(seconds / 86400);
        var hours = Math.floor((seconds % 86400) / 3600);
        var minutes = Math.floor((seconds % 3600) / 60);
        var parts: string[] = [];
        if (days > 0) parts.push(fm('installation.detail.duration.days', '{count} ditë', { count: days }));
        if (hours > 0) parts.push(fm('installation.detail.duration.hours', '{count} orë', { count: hours }));
        if (minutes > 0 || parts.length === 0) parts.push(fm('installation.detail.duration.minutes', '{count} min', { count: minutes }));
        return parts.join(', ');
    };

    var getStatusLabel = function(s: number) {
        if (s === 1) return fm('service.strip.registered', 'E regjistruar');
        if (s === 2) return fm('service.strip.diagnosing', 'Në diagnostikim');
        if (s === 3) return fm('service.map.waitingMaterial', 'Në pritje të materialit');
        if (s === 4) return fm('service.strip.repairing', 'Në riparim');
        if (s === 5) return fm('service.map.waitingClient', 'Në pritje të klientit');
        if (s === 6) return fm('service.strip.completed', 'E përfunduar');
        return '';
    };

    var getPriorityLabel = function(p: number) {
        if (p === 1) return fm('service.priority.low', 'E ulët');
        if (p === 2) return fm('service.priority.normal', 'Normale');
        if (p === 3) return fm('service.priority.high', 'E lartë');
        if (p === 4) return fm('service.priority.urgent', 'Urgjente');
        return '';
    };

    var translateTypeName = function(name: string | null | undefined): string {
        if (!name) return '';
        var n = name.toLowerCase().trim();
        if (n === 'kontroll') return fm('service.type.kontroll', 'Kontroll');
        if (n.indexOf('thjesht') !== -1) return fm('service.type.servisimSimple', 'Servisim i thjeshtë');
        if (n.indexOf('komplikuar') !== -1) return fm('service.type.servisimComplex', 'Servisim i komplikuar');
        return name;
    };

    // Build timeline entry description from eventType + metadata.
    // Backend-provided `description` is Albanian; we reconstruct it locale-aware.
    var formatEvent = function(entry: any): string {
        var t = entry?.eventType;
        var meta = entry?.metadata || {};
        if (t === 'created') return fm('service.detail.event.created', 'Servisimi u krijua');
        if (t === 'status_changed') {
            var newStatus = meta.new_status ?? meta.to_status ?? meta.status;
            if (typeof newStatus === 'number') {
                var label = getStatusLabel(newStatus);
                if (label) return fm('service.detail.event.statusChangedTo', 'Statusi u ndryshua në: {status}', { status: label });
            }
            return fm('service.detail.event.statusChanged', 'Statusi u ndryshua');
        }
        if (t === 'file_uploaded') return fm('service.detail.event.fileUploaded', 'Dokumenti u ngarkua');
        if (t === 'file_deleted') return fm('service.detail.event.fileDeleted', 'Dokumenti u fshi');
        if (t === 'edited') return fm('service.detail.event.edited', 'Servisimi u përditësua');
        return entry?.description || '';
    };

    var servicingId = parseInt(props.match.params.servicingId);
    var [newCommentText, setNewCommentText] = useState('');
    var [newDiagnosisText, setNewDiagnosisText] = useState('');
    var [newResolutionText, setNewResolutionText] = useState('');
    var [liveActiveSeconds, setLiveActiveSeconds] = useState(0);
    var [showVisits, setShowVisits] = useState(false);
    var [transitionConfirm, setTransitionConfirm] = useState<{ status: number; label: string } | null>(null);
    var [blockNoteModal, setBlockNoteModal] = useState<{ status: number; label: string } | null>(null);
    var [blockNoteText, setBlockNoteText] = useState('');
    var [activeFileTab, setActiveFileTab] = useState<string>('before');
    var [deleteFileConfirm, setDeleteFileConfirm] = useState<{ id: number; name: string } | null>(null);
    var [editOpen, setEditOpen] = useState(false);
    var [editForm, setEditForm] = useState<any>({});
    var [editTypes, setEditTypes] = useState<any[]>([]);
    var [savingEdit, setSavingEdit] = useState(false);
    var [voidConfirm, setVoidConfirm] = useState(false);
    var [expandedProduct, setExpandedProduct] = useState<number | null>(null);
    var [productHistory, setProductHistory] = useState<any[]>([]);

    var svc = props.currentServicing;

    useEffect(function() { if (servicingId) props.actions.fetchServicing(servicingId); }, [servicingId]);

    // Live timer
    useEffect(function() {
        if (svc?.activeSeconds !== undefined) setLiveActiveSeconds(svc.activeSeconds);
    }, [svc?.activeSeconds]);

    useEffect(function() {
        var isActive = svc?.status === 2 || svc?.status === 4;
        if (!isActive) return;
        var interval = setInterval(function() { setLiveActiveSeconds(function(p: number) { return p + 60; }); }, 60000);
        return function() { clearInterval(interval); };
    }, [svc?.status]);

    if (!svc) return <div className="clp-page" style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>{fm('service.detail.loading', 'Duke u ngarkuar...')}</div>;

    var statusColors = SERVICING_STATUS_COLORS[svc.status] || { color: '#94a3b8', bg: '#f1f5f9' };
    var priorityColors = SERVICING_PRIORITY_COLORS[svc.priority] || { color: '#3b82f6', bg: '#dbeafe' };
    var isActive = svc.status === 2 || svc.status === 4;
    var allowedNext = ALLOWED_TRANSITIONS[svc.status] || [];

    var handleTransition = function(newStatus: number) {
        // Waiting states need block note
        if (newStatus === 3 || newStatus === 5) {
            var tCfg = TRANSITION_I18N[newStatus];
            setBlockNoteModal({ status: newStatus, label: tCfg ? fm(tCfg.id, tCfg.defaultMessage) : '' });
            return;
        }
        // Completion needs confirm
        if (newStatus === 6) {
            setTransitionConfirm({ status: 6, label: fm('service.detail.confirm.finishTitle', 'Përfundo servisimin') });
            return;
        }
        // Active states — transition immediately
        doTransition(newStatus, '');
    };

    var doTransition = function(newStatus: number, blockNote: string) {
        props.actions.transitionServicing(servicingId, { status: newStatus, block_note: blockNote || undefined }).then(function(result: any) {
            if (result?.error) { toast.error(result.message || fm('service.detail.toast.genericError', 'Gabim')); return; }
            toast.success(getStatusLabel(newStatus) || fm('service.detail.toast.statusChanged', 'Statusi u ndryshua'));
            props.actions.fetchServicing(servicingId);
        });
    };

    var permissions = props.permissions || {};
    var canManage = !!((permissions.fullPrivileges || permissions.service) && !permissions.observer);

    var setEf = function(key: string, val: any) {
        setEditForm(function(prev: any) { var n: any = {}; Object.keys(prev).forEach(function(k) { n[k] = prev[k]; }); n[key] = val; return n; });
    };

    var openEdit = function() {
        setEditForm({
            priority: svc.priority,
            servicing_type_id: svc.servicingType ? svc.servicingType.id : '',
            is_warranty: !!svc.isWarranty,
            warranty_expiry_date: svc.warrantyExpiryDate ? String(svc.warrantyExpiryDate).substring(0, 10) : '',
            servicing_location: svc.servicingLocation || '',
            diagnosis: svc.diagnosis || '',
        });
        axiosCaller('GET', 'servicing-types').then(function(res: any) { setEditTypes(res?.data || []); });
        setEditOpen(true);
    };

    var saveEdit = function() {
        setSavingEdit(true);
        var payload: any = {
            priority: editForm.priority,
            servicing_type_id: editForm.servicing_type_id || null,
            is_warranty: editForm.is_warranty ? 1 : 0,
            warranty_expiry_date: editForm.is_warranty && editForm.warranty_expiry_date ? editForm.warranty_expiry_date : null,
            servicing_location: editForm.servicing_location,
        };
        if (svc.status === 1) payload.diagnosis = editForm.diagnosis;
        props.actions.editServicing(servicingId, payload).then(function(res: any) {
            setSavingEdit(false);
            if (res && res.error) { toast.error(res.message || fm('service.detail.toast.genericError', 'Gabim')); return; }
            setEditOpen(false);
            toast.success(fm('service.detail.toast.edited', 'Servisimi u përditësua'));
            props.actions.fetchServicing(servicingId);
        });
    };

    var doVoid = function() {
        props.actions.voidServicing(servicingId).then(function(res: any) {
            setVoidConfirm(false);
            if (res && res.data) {
                toast.success(fm('service.detail.toast.voided', 'Servisimi u anulua'));
                props.history.push('/dashboard/service');
            } else {
                toast.error(fm('service.detail.toast.genericError', 'Gabim'));
            }
        });
    };

    var toggleProductHistory = function(productId: number) {
        if (expandedProduct === productId) { setExpandedProduct(null); return; }
        setExpandedProduct(productId);
        setProductHistory([]);
        props.actions.fetchProductHistory(productId).then(function(data: any) { setProductHistory(data || []); });
    };

    var handleAddNote = function(text: string, type: string, clearFn: Function) {
        if (!text.trim()) return;
        props.actions.addServicingComment(servicingId, text.trim(), type).then(function() {
            clearFn('');
            props.actions.fetchServicing(servicingId);
        });
    };

    var handleAddComment = function() {
        if (!newCommentText.trim()) return;
        props.actions.addServicingComment(servicingId, newCommentText.trim(), 'comment').then(function() {
            setNewCommentText('');
            props.actions.fetchServicing(servicingId);
        });
    };

    var handleFileUpload = function(e: React.ChangeEvent<HTMLInputElement>) {
        var files = e.target.files;
        if (!files || files.length === 0) return;
        var formData = new FormData();
        for (var i = 0; i < files.length; i++) {
            formData.append('servicing_files', files[i]);
        }
        formData.append('file_category', activeFileTab);
        props.actions.uploadServicingFiles(servicingId, formData).then(function() {
            props.actions.fetchServicing(servicingId);
        });
        e.target.value = '';
    };

    var handleFileDelete = function() {
        if (!deleteFileConfirm) return;
        props.actions.deleteServicingFile(servicingId, deleteFileConfirm.id).then(function() {
            setDeleteFileConfirm(null);
            props.actions.fetchServicing(servicingId);
        });
    };

    var fileTabs = [
        { key: 'before', label: fm('service.detail.fileTab.before', 'Para servisimit') },
        { key: 'diagnosis', label: fm('service.detail.fileTab.diagnosis', 'Diagnoza') },
        { key: 'after', label: fm('service.detail.fileTab.after', 'Pas servisimit') },
        { key: 'other', label: fm('service.detail.fileTab.other', 'Tjetër') },
    ];

    var filesByCategory = function(cat: string) {
        return (svc.files || []).filter(function(f: any) { return (f.category || 'other') === cat; });
    };

    return (
        <div className="clp-page" style={{ gap: 12 }}>
            <MikaPageHeader
                title={fm('service.detail.pageTitle', 'Servisimi #{id}', { id: svc.id })}
                actions={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: statusColors.color, background: statusColors.bg }}>{getStatusLabel(svc.status) || svc.statusLabel}</span>
                        <span style={{ padding: '4px 10px', borderRadius: 12, fontSize: 10, fontWeight: 600, color: priorityColors.color, background: priorityColors.bg }}>{getPriorityLabel(svc.priority) || svc.priorityLabel}</span>
                        {svc.isWarranty && (
                            <span style={{ padding: '3px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, color: svc.isWarrantyActive ? '#16a34a' : '#94a3b8', background: svc.isWarrantyActive ? '#f0fdf4' : '#f1f5f9' }}>
                                {svc.isWarrantyActive ? fm('service.detail.warrantyActiveBadge', 'Nën garanci') : fm('service.detail.warrantyExpiredBadge', 'Garanci e skaduar')}
                            </span>
                        )}
                        {allowedNext.map(function(nextStatus: number) {
                            var btnConfig = TRANSITION_I18N[nextStatus];
                            if (!btnConfig) return null;
                            return (
                                <button key={nextStatus} className="inst-action-btn"
                                    style={{ background: btnConfig.bg, color: '#fff', border: 'none', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}
                                    onClick={function() { handleTransition(nextStatus); }}>
                                    {fm(btnConfig.id, btnConfig.defaultMessage)}
                                </button>
                            );
                        })}
                        {canManage && svc.status !== 6 && (
                            <button className="inst-action-btn" onClick={openEdit}
                                style={{ background: '#fff', color: '#1e3a5f', border: '1px solid #cbd5e1' }}>
                                {fm('service.detail.editAction', 'Modifiko')}
                            </button>
                        )}
                        {canManage && (
                            <button className="inst-action-btn" onClick={function() { setVoidConfirm(true); }}
                                style={{ background: '#fff', color: '#ef4444', border: '1px solid #fecaca' }}>
                                {fm('service.detail.voidAction', 'Anulo')}
                            </button>
                        )}
                    </div>
                }
            />

            {/* Block note modal (for waiting states) */}
            {blockNoteModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                    onClick={function() { setBlockNoteModal(null); setBlockNoteText(''); }}>
                    <div style={{ background: '#fff', borderRadius: 14, padding: 0, width: 440, maxWidth: '100%', boxShadow: '0 24px 64px rgba(15,23,42,0.2)', animation: 'mikaScaleIn 0.2s ease', overflow: 'hidden' }}
                        onClick={function(e) { e.stopPropagation(); }}>
                        <div style={{ padding: '18px 22px 0' }}>
                            <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{blockNoteModal.label}</h3>
                            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}><FormattedMessage id="service.detail.blockNote.subtitle" defaultMessage="Shkruaj arsyen pse duhet të presim" /></p>
                        </div>
                        <div style={{ padding: '0 22px 22px' }}>
                            <textarea value={blockNoteText} onChange={function(e) { setBlockNoteText(e.target.value); }} autoFocus
                                placeholder={fm('service.detail.blockNote.placeholder', 'Arsyeja...')}
                                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8', fontSize: 13, minHeight: 70, resize: 'vertical', outline: 'none', boxSizing: 'border-box' as any }} />
                        </div>
                        <div style={{ padding: '14px 22px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button onClick={function() { setBlockNoteModal(null); setBlockNoteText(''); }} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, cursor: 'pointer', color: '#64748b', fontWeight: 500 }}><FormattedMessage id="service.detail.blockNote.cancel" defaultMessage="Anulo" /></button>
                            <button onClick={function() {
                                doTransition(blockNoteModal!.status, blockNoteText.trim());
                                setBlockNoteModal(null); setBlockNoteText('');
                            }} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: TRANSITION_I18N[blockNoteModal.status]?.bg || '#f59e0b', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}><FormattedMessage id="service.detail.blockNote.confirm" defaultMessage="Konfirmo" /></button>
                        </div>
                    </div>
                </div>
            )}

            {/* Transition confirm modal (for completion) */}
            <MikaConfirmModal
                isOpen={!!transitionConfirm}
                onClose={function() { setTransitionConfirm(null); }}
                onConfirm={function() { doTransition(transitionConfirm!.status, ''); setTransitionConfirm(null); }}
                title={transitionConfirm?.label || ''}
                message={fm('service.detail.confirm.finishMessage', 'Jeni të sigurt që doni të përfundoni servisimin?')}
                confirmLabel={fm('service.detail.confirm.finish', 'Përfundo')}
                confirmColor="primary"
            />

            {/* Void confirm */}
            <MikaConfirmModal
                isOpen={voidConfirm}
                onClose={function() { setVoidConfirm(false); }}
                onConfirm={doVoid}
                title={fm('service.detail.voidTitle', 'Anulo servisimin')}
                message={fm('service.detail.voidMessage', 'Jeni të sigurt? Servisimi do të hiqet nga lista.')}
                confirmLabel={fm('service.detail.voidConfirm', 'Anulo servisimin')}
                confirmColor="danger"
            />

            {/* Edit modal */}
            {editOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                    onClick={function() { setEditOpen(false); }}>
                    <div style={{ background: '#fff', borderRadius: 14, width: 480, maxWidth: '100%', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 24px 64px rgba(15,23,42,0.2)', animation: 'mikaScaleIn 0.2s ease' }}
                        onClick={function(e) { e.stopPropagation(); }}>
                        <div style={{ padding: '18px 22px 0' }}>
                            <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{fm('service.detail.editTitle', 'Modifiko servisimin')}</h3>
                            {svc.status !== 1 && <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 8px' }}>{fm('service.detail.editLockedHint', 'Pas fillimit të punës, vetëm fushat bazë mund të ndryshohen.')}</p>}
                        </div>
                        <div style={{ padding: '12px 22px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div>
                                <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>{fm('service.form.servicingType', 'Lloji i servisimit')}</label>
                                <select value={editForm.servicing_type_id || ''} onChange={function(e) { setEf('servicing_type_id', e.target.value); }}
                                    style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8', fontSize: 13, background: '#fff' }}>
                                    <option value="">{fm('service.form.servicingTypePlaceholder', 'Zgjidh llojin...')}</option>
                                    {editTypes.map(function(t: any) { return <option key={t.id} value={t.id}>{translateTypeName(t.name)}</option>; })}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>{fm('service.form.priority', 'Prioriteti')}</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6 }}>
                                    {[1, 2, 3, 4].map(function(pv) {
                                        var isSel = editForm.priority === pv;
                                        var pc = SERVICING_PRIORITY_COLORS[pv] || { color: '#3b82f6', bg: '#dbeafe' };
                                        return (
                                            <button key={pv} onClick={function() { setEf('priority', pv); }}
                                                style={{ padding: '8px 4px', borderRadius: 6, fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer', background: isSel ? pc.color : '#f1f5f9', color: isSel ? '#fff' : '#64748b' }}>
                                                {getPriorityLabel(pv)}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#0f172a' }}>
                                    <input type="checkbox" checked={!!editForm.is_warranty} onChange={function(e) { setEf('is_warranty', e.target.checked); }} style={{ width: 16, height: 16 } as any} />
                                    {fm('service.form.warrantyToggle', 'Nën garanci')}
                                </label>
                                {editForm.is_warranty && (
                                    <input type="date" value={editForm.warranty_expiry_date || ''} onChange={function(e) { setEf('warranty_expiry_date', e.target.value); }}
                                        style={{ marginTop: 8, width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8', fontSize: 13 }} />
                                )}
                            </div>
                            <div>
                                <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>{fm('service.form.servicingLocation', 'Vendndodhja e servisimit')}</label>
                                <input value={editForm.servicing_location || ''} onChange={function(e) { setEf('servicing_location', e.target.value); }}
                                    style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8', fontSize: 13 }} />
                            </div>
                            {svc.status === 1 && (
                                <div>
                                    <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>{fm('service.form.diagnosisLabel', 'Diagnostika')}</label>
                                    <textarea value={editForm.diagnosis || ''} onChange={function(e) { setEf('diagnosis', e.target.value); }}
                                        style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8', fontSize: 13, minHeight: 80, resize: 'vertical' as any }} />
                                </div>
                            )}
                        </div>
                        <div style={{ padding: '14px 22px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button onClick={function() { setEditOpen(false); }} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, cursor: 'pointer', color: '#64748b', fontWeight: 500 }}>{fm('service.detail.blockNote.cancel', 'Anulo')}</button>
                            <button onClick={saveEdit} disabled={savingEdit} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 600, cursor: savingEdit ? 'wait' : 'pointer', opacity: savingEdit ? 0.6 : 1 }}>{fm('common.action.save', 'Ruaj')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Visits popup */}
            {showVisits && svc.visits && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                    onClick={function() { setShowVisits(false); }}>
                    <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 500, maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 24px 64px rgba(15,23,42,0.2)', animation: 'mikaScaleIn 0.2s ease' }}
                        onClick={function(e) { e.stopPropagation(); }}>
                        <div style={{ padding: '18px 22px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{fm('service.detail.visits.title', 'Historiku i vizitave ({count})', { count: svc.visits.length })}</h3>
                            <button onClick={function() { setShowVisits(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                            </button>
                        </div>
                        <div style={{ padding: '0 22px 22px', maxHeight: '60vh', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {svc.visits.map(function(v: any) {
                                var reasonLabels: Record<string, string> = {
                                    waiting_material: fm('service.detail.visits.reason.waitingMaterial', 'Prit materialin'),
                                    waiting_client: fm('service.detail.visits.reason.waitingClient', 'Prit klientin'),
                                    finished: fm('service.detail.visits.reason.finished', 'Përfunduar'),
                                    paused: fm('service.detail.visits.reason.paused', 'Ndërpritur'),
                                };
                                var borderColors: Record<string, string> = { waiting_material: '#f59e0b', waiting_client: '#f97316', finished: '#22c55e', paused: '#94a3b8' };
                                var bgColors: Record<string, string> = { waiting_material: '#fef3c7', waiting_client: '#fff7ed', finished: '#f0fdf4', paused: '#f8fafc' };
                                var isVisitActive = !v.endedAt;
                                var borderColor = isVisitActive ? '#3b82f6' : (borderColors[v.endedReason] || '#e2e8f0');
                                var bgColor = isVisitActive ? '#eff6ff' : (bgColors[v.endedReason] || '#f8fafc');

                                return (
                                    <div key={v.id} style={{ borderRadius: 8, border: '1px solid ' + (isVisitActive ? '#bfdbfe' : '#e2e8f0'), borderLeft: '3px solid ' + borderColor, background: bgColor, padding: '10px 14px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                            <span style={{ width: 24, height: 24, borderRadius: 6, background: isVisitActive ? '#dbeafe' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: isVisitActive ? '#3b82f6' : '#64748b', flexShrink: 0 }}>{v.visitNumber}</span>
                                            {isVisitActive ? (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: '#3b82f6' }}><span className="inst-kpi-live" style={{ marginLeft: 0 }}></span><FormattedMessage id="service.detail.visits.inProgress" defaultMessage="Në progres" /></span>
                                            ) : v.endedReason ? (
                                                <span style={{ fontSize: 11, fontWeight: 600, color: borderColors[v.endedReason] || '#64748b' }}>{reasonLabels[v.endedReason] || v.endedReason}</span>
                                            ) : null}
                                            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: '#64748b', padding: '2px 8px', borderRadius: 6, background: isVisitActive ? '#dbeafe' : '#f1f5f9' }}>
                                                {isVisitActive ? <span style={{ color: '#3b82f6' }}>{fm('service.detail.visits.active', 'aktiv')}</span> : (v.formattedDuration || fm('installation.detail.duration.zeroMin', '0 min'))}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 34, fontSize: 12, color: '#64748b' }}>
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                            <span>{fmtDtShort(v.startedAt)}</span>
                                            <span style={{ color: '#d0dbe8' }}>→</span>
                                            <span>{isVisitActive ? fm('service.detail.visits.now', 'tani') : fmtDtShort(v.endedAt)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>

                {/* KPI Strip */}
                <div className="inst-kpi-strip">
                    <div className="inst-kpi-card">
                        <div className="inst-kpi-icon" style={{ background: '#eef3f9', color: '#1e3a5f' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        </div>
                        <div className="inst-kpi-info">
                            <div className="inst-kpi-value">{svc.technician?.name || '-'}</div>
                            <div className="inst-kpi-label">{fm('service.col.technician', 'Tekniku')}</div>
                        </div>
                    </div>
                    <div className="inst-kpi-card">
                        <div className="inst-kpi-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        </div>
                        <div className="inst-kpi-info">
                            <div className="inst-kpi-value">{formatActiveSeconds(liveActiveSeconds)}{isActive && <span className="inst-kpi-live" />}</div>
                            <div className="inst-kpi-label">{fm('service.col.activeDuration', 'Koha aktive')}</div>
                        </div>
                    </div>
                    <div className="inst-kpi-card" style={{ borderColor: priorityColors.bg }}>
                        <div className="inst-kpi-icon" style={{ background: priorityColors.bg, color: priorityColors.color }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        </div>
                        <div className="inst-kpi-info">
                            <div className="inst-kpi-value" style={{ color: priorityColors.color }}>{getPriorityLabel(svc.priority) || svc.priorityLabel}</div>
                            <div className="inst-kpi-label">{fm('service.col.priority', 'Prioriteti')}</div>
                        </div>
                    </div>
                    {svc.isWarranty && (
                        <div className="inst-kpi-card" style={{ borderColor: svc.isWarrantyActive ? '#bbf7d0' : '#fecaca' }}>
                            <div className="inst-kpi-icon" style={{ background: svc.isWarrantyActive ? '#f0fdf4' : '#fef2f2', color: svc.isWarrantyActive ? '#22c55e' : '#ef4444' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                            </div>
                            <div className="inst-kpi-info">
                                <div className="inst-kpi-value" style={{ color: svc.isWarrantyActive ? '#22c55e' : '#ef4444' }}>
                                    {svc.isWarrantyActive ? fm('service.detail.kpi.warrantyActive', 'Aktive') : fm('service.detail.kpi.warrantyExpired', 'E skaduar')}
                                </div>
                                <div className="inst-kpi-label">{svc.warrantyExpiryDate ? fm('service.detail.kpi.warrantyLabelWithDate', 'Garancia — {date}', { date: fmtDateOnly(svc.warrantyExpiryDate) }) : fm('service.detail.kpi.warrantyLabel', 'Garancia')}</div>
                            </div>
                        </div>
                    )}
                    {svc.servicingType && (
                        <div className="inst-kpi-card">
                            <div className="inst-kpi-icon" style={{ background: '#ede9fe', color: '#8b5cf6' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                            </div>
                            <div className="inst-kpi-info">
                                <div className="inst-kpi-value">{translateTypeName(svc.servicingType.name)}</div>
                                <div className="inst-kpi-label">{fm('service.col.type', 'Lloji')}</div>
                            </div>
                        </div>
                    )}
                    {svc.visits && svc.visits.length > 0 && (
                        <div className="inst-kpi-card" style={{ borderColor: '#bfdbfe', cursor: 'pointer' }} onClick={function() { setShowVisits(true); }}>
                            <div className="inst-kpi-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                            </div>
                            <div className="inst-kpi-info">
                                <div className="inst-kpi-value" style={{ color: '#3b82f6' }}>{svc.visits.length}</div>
                                <div className="inst-kpi-label">{fm('service.detail.kpi.visits', 'Vizita')}</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Block note banner */}
                {svc.blockNote && (svc.status === 3 || svc.status === 5) && (
                    <div className="inst-block-banner">
                        <div className="inst-block-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        </div>
                        <div className="inst-block-text">
                            <div className="inst-block-label">{svc.status === 3 ? fm('service.map.waitingMaterial', 'Në pritje të materialit') : fm('service.map.waitingClient', 'Në pritje të klientit')}</div>
                            <div className="inst-block-reason">{svc.blockNote}</div>
                        </div>
                    </div>
                )}

                {/* Main content */}
                <div className="inst-detail-main">
                    <div className="inst-detail-content">

                        {/* Client & Project card */}
                        {svc.client && (
                            <div>
                                <div className="t-card-header">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                    <FormattedMessage id="service.detail.clientProjectHeader" defaultMessage="Klienti & Projekti" />
                                </div>
                                <div className="t-card-body">
                                    <div className="t-delivery-info">
                                        <div className="t-delivery-field">
                                            <div className="t-delivery-label"><FormattedMessage id="service.form.fieldClient" defaultMessage="Klienti" /></div>
                                            <div className="t-delivery-value">{svc.client.name || '—'}</div>
                                        </div>
                                        <div className="t-delivery-field">
                                            <div className="t-delivery-label"><FormattedMessage id="service.form.projectHeader" defaultMessage="Projekti" /></div>
                                            <div className="t-delivery-value">{svc.project?.name || '—'}</div>
                                        </div>
                                        <div className="t-delivery-field">
                                            <div className="t-delivery-label"><FormattedMessage id="service.form.fieldPhone" defaultMessage="Telefoni" /></div>
                                            <div className="t-delivery-value">{svc.client.phone || '—'}</div>
                                        </div>
                                        <div className="t-delivery-field">
                                            <div className="t-delivery-label"><FormattedMessage id="service.form.fieldCity" defaultMessage="Qyteti" /></div>
                                            <div className="t-delivery-value">{svc.client.city || '—'}</div>
                                        </div>
                                        <div className="t-delivery-field">
                                            <div className="t-delivery-label"><FormattedMessage id="service.form.fieldCountry" defaultMessage="Shteti" /></div>
                                            <div className="t-delivery-value">{svc.client.country || '—'}</div>
                                        </div>
                                        <div className="t-delivery-field">
                                            <div className="t-delivery-label"><FormattedMessage id="service.form.fieldAddress" defaultMessage="Adresa" /></div>
                                            <div className="t-delivery-value">{svc.client.address || '—'}</div>
                                        </div>
                                        {svc.measureLocation && parseCoords(svc.measureLocation) && (
                                            <div className="t-delivery-field">
                                                <div className="t-delivery-label"><FormattedMessage id="service.form.fieldLocation" defaultMessage="Lokacioni" /></div>
                                                <div className="t-delivery-value">
                                                    <a href={'https://www.google.com/maps/dir/?api=1&destination=' + parseCoords(svc.measureLocation)![0] + ',' + parseCoords(svc.measureLocation)![1]}
                                                        target="_blank" rel="noopener noreferrer" className="t-maps-link">
                                                        <svg width="11" height="14" viewBox="3 1 18 21" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                                        {fm('service.form.googleMaps', 'Google Maps')}
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Diagnosis thread */}
                        <div>
                            <div className="t-card-header" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4a7a 100%)' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                {fm('service.detail.diagnosisHeader', 'Diagnostika ({count})', { count: (svc.diagnosisNotes || []).length })}
                            </div>
                            <div className="t-card-body" style={{ padding: '10px 14px' }}>
                                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                                    <input type="text" value={newDiagnosisText} onChange={function(e) { setNewDiagnosisText(e.target.value); }}
                                        onKeyDown={function(e) { if (e.key === 'Enter') handleAddNote(newDiagnosisText, 'diagnosis', setNewDiagnosisText); }}
                                        placeholder={fm('service.detail.diagnosisPlaceholder', 'Shkruaj diagnostikë...')}
                                        style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #bfdbfe', fontSize: 12, outline: 'none', background: '#f0f9ff' }} />
                                    <button onClick={function() { handleAddNote(newDiagnosisText, 'diagnosis', setNewDiagnosisText); }} disabled={!newDiagnosisText.trim()}
                                        style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: newDiagnosisText.trim() ? '#3b82f6' : '#e2e8f0', color: newDiagnosisText.trim() ? '#fff' : '#94a3b8', fontSize: 11, fontWeight: 600, cursor: newDiagnosisText.trim() ? 'pointer' : 'default' }}>
                                        <FormattedMessage id="service.form.addComment" defaultMessage="Shto" />
                                    </button>
                                </div>
                                <div style={{ maxHeight: 200, overflow: 'auto' }}>
                                    {(svc.diagnosisNotes || []).map(function(c: any) {
                                        return (
                                            <div key={c.id} style={{ padding: '8px 10px', marginBottom: 4, borderRadius: 6, background: '#f0f9ff', border: '1px solid #dbeafe', fontSize: 12 }}>
                                                <div style={{ color: '#0f172a', lineHeight: 1.5 }}><MentionText text={c.text} /></div>
                                                <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>{c.user?.name || fm('installation.detail.unknownUser', 'I panjohur')} — {fmtDate(c.createdAt)}</div>
                                            </div>
                                        );
                                    })}
                                    {(svc.diagnosisNotes || []).length === 0 && <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>{fm('service.detail.diagnosisEmpty', 'Nuk ka diagnostikë ende')}</div>}
                                </div>
                            </div>
                        </div>

                        {/* Resolution thread */}
                        <div>
                            <div className="t-card-header" style={{ background: 'linear-gradient(135deg, #14532d 0%, #166534 100%)' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                                {fm('service.detail.resolutionHeader', 'Rezolucioni ({count})', { count: (svc.resolutionNotes || []).length })}
                            </div>
                            <div className="t-card-body" style={{ padding: '10px 14px' }}>
                                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                                    <input type="text" value={newResolutionText} onChange={function(e) { setNewResolutionText(e.target.value); }}
                                        onKeyDown={function(e) { if (e.key === 'Enter') handleAddNote(newResolutionText, 'resolution', setNewResolutionText); }}
                                        placeholder={fm('service.detail.resolutionPlaceholder', 'Shkruaj rezolucion...')}
                                        style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #bbf7d0', fontSize: 12, outline: 'none', background: '#f0fdf4' }} />
                                    <button onClick={function() { handleAddNote(newResolutionText, 'resolution', setNewResolutionText); }} disabled={!newResolutionText.trim()}
                                        style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: newResolutionText.trim() ? '#22c55e' : '#e2e8f0', color: newResolutionText.trim() ? '#fff' : '#94a3b8', fontSize: 11, fontWeight: 600, cursor: newResolutionText.trim() ? 'pointer' : 'default' }}>
                                        <FormattedMessage id="service.form.addComment" defaultMessage="Shto" />
                                    </button>
                                </div>
                                <div style={{ maxHeight: 200, overflow: 'auto' }}>
                                    {(svc.resolutionNotes || []).map(function(c: any) {
                                        return (
                                            <div key={c.id} style={{ padding: '8px 10px', marginBottom: 4, borderRadius: 6, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 12 }}>
                                                <div style={{ color: '#0f172a', lineHeight: 1.5 }}><MentionText text={c.text} /></div>
                                                <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>{c.user?.name || fm('installation.detail.unknownUser', 'I panjohur')} — {fmtDate(c.createdAt)}</div>
                                            </div>
                                        );
                                    })}
                                    {(svc.resolutionNotes || []).length === 0 && <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>{fm('service.detail.resolutionEmpty', 'Nuk ka rezolucion ende')}</div>}
                                </div>
                            </div>
                        </div>

                        {/* Products */}
                        {svc.productsServicing && svc.productsServicing.length > 0 && (
                            <div>
                                <div className="t-card-header">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                                    {fm('service.detail.productsHeader', 'Produkte ({count})', { count: svc.productsServicing.length })}
                                </div>
                                <div className="t-card-body" style={{ padding: 0 }}>
                                    {svc.productsServicing.map(function(ps: any) {
                                        var isExp = expandedProduct === ps.productId;
                                        return (
                                            <div key={ps.id}>
                                                <div className="t-product-row">
                                                    <span className="t-product-name">{ps.productName || ps.articleName || '—'}</span>
                                                    {ps.type && <span className="t-product-type">{ps.type}</span>}
                                                    {ps.articleCode && <span className="t-product-type">{ps.articleCode}</span>}
                                                    <span className="t-product-qty">{ps.quantity || ''} {ps.unit || ''}</span>
                                                    {ps.productId && (
                                                        <button onClick={function() { toggleProductHistory(ps.productId); }}
                                                            style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: 11, fontWeight: 600 }}>
                                                            {fm('service.detail.productHistoryLink', 'Historiku')}
                                                        </button>
                                                    )}
                                                </div>
                                                {isExp && (
                                                    <div style={{ padding: '6px 14px 10px', background: '#f8fafc' }}>
                                                        {productHistory.filter(function(h: any) { return h.id !== svc.id; }).length === 0 && (
                                                            <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>{fm('service.detail.productHistoryEmpty', 'Asnjë servisim tjetër për këtë produkt')}</div>
                                                        )}
                                                        {productHistory.filter(function(h: any) { return h.id !== svc.id; }).map(function(h: any) {
                                                            return (
                                                                <a key={h.id} href={'/dashboard/service/' + h.id}
                                                                    style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', fontSize: 11, color: '#475569', textDecoration: 'none', borderBottom: '1px solid #eef2f7' }}>
                                                                    <span style={{ color: '#64748b', minWidth: 70 }}>{fmtDateOnly(h.createdAt)}</span>
                                                                    <span style={{ fontWeight: 600 }}>{getStatusLabel(h.status)}</span>
                                                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as any }}>{h.diagnosis || ''}</span>
                                                                </a>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Agreement files */}
                        {svc.agreementFiles && svc.agreementFiles.length > 0 && (
                            <div>
                                <div className="t-card-header">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                    {fm('service.detail.agreementFilesHeader', 'Dokumentet e marrëveshjes ({count})', { count: svc.agreementFiles.length })}
                                </div>
                                <div className="t-card-body" style={{ padding: 0 }}>
                                    {svc.agreementFiles.map(function(af: any) {
                                        return (
                                            <div key={af.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: '1px solid #f3f4f6' }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                                <a href={af.fileUrl} target="_blank" rel="noopener noreferrer"
                                                    style={{ flex: 1, fontSize: 12, color: '#1e3a5f', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as any }}>
                                                    {af.fileName || fm('service.detail.documentFallback', 'Dokument')}
                                                </a>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="inst-detail-sidebar">
                        {/* Comments */}
                        <div>
                            <div className="t-card-header">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                {fm('service.detail.commentsHeader', 'Komentet ({count})', { count: (svc.comments || []).length })}
                            </div>
                            <div className="t-card-body" style={{ padding: '10px 14px' }}>
                                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                                    <MentionInput
                                        type="text"
                                        value={newCommentText}
                                        onChange={function(next) { setNewCommentText(next); }}
                                        onKeyDown={function(e) { if (e.key === 'Enter') handleAddComment(); }}
                                        placeholder={fm('service.form.commentPlaceholder', 'Shkruaj koment...')}
                                        style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #d0dbe8', fontSize: 12, outline: 'none' }}
                                    />
                                    <button onClick={handleAddComment} disabled={!newCommentText.trim()}
                                        style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: newCommentText.trim() ? '#1e3a5f' : '#e2e8f0', color: newCommentText.trim() ? '#fff' : '#94a3b8', fontSize: 11, fontWeight: 600, cursor: newCommentText.trim() ? 'pointer' : 'default' }}>
                                        <FormattedMessage id="service.form.addComment" defaultMessage="Shto" />
                                    </button>
                                </div>
                                <div style={{ maxHeight: 200, overflow: 'auto' }}>
                                    {(svc.comments || []).map(function(c: any) {
                                        return (
                                            <div key={c.id} style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                                                <div style={{ color: '#0f172a', lineHeight: 1.5 }}><MentionText text={c.text} /></div>
                                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{c.user?.name || fm('installation.detail.unknownUser', 'I panjohur')} — {fmtDate(c.createdAt)}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Files with categories */}
                        <div>
                            <div className="t-card-header">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                {fm('service.detail.filesHeader', 'Dokumentet ({count})', { count: (svc.files || []).length })}
                            </div>
                            <div className="t-card-body" style={{ padding: '10px 14px' }}>
                                {/* Category tabs */}
                                <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
                                    {fileTabs.map(function(tab) {
                                        var count = filesByCategory(tab.key).length;
                                        var isActive = activeFileTab === tab.key;
                                        return (
                                            <button key={tab.key} onClick={function() { setActiveFileTab(tab.key); }}
                                                style={{
                                                    padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                                                    border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                                                    background: isActive ? '#1e3a5f' : '#f1f5f9',
                                                    color: isActive ? '#fff' : '#64748b',
                                                }}>
                                                {tab.label}{count > 0 ? ' (' + count + ')' : ''}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Upload button */}
                                <label style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                    padding: '8px 12px', borderRadius: 6, border: '1px dashed #d0dbe8',
                                    cursor: 'pointer', background: '#fafbfc', fontSize: 11, color: '#64748b',
                                    transition: 'all 0.15s', marginBottom: 8,
                                }}
                                onMouseEnter={function(e: any) { e.currentTarget.style.borderColor = '#1e3a5f'; e.currentTarget.style.color = '#1e3a5f'; }}
                                onMouseLeave={function(e: any) { e.currentTarget.style.borderColor = '#d0dbe8'; e.currentTarget.style.color = '#64748b'; }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                    <FormattedMessage id="service.detail.uploadShort" defaultMessage="Ngarko" />
                                    <input type="file" multiple accept="image/*" onChange={handleFileUpload} style={{ display: 'none' }} />
                                </label>

                                {/* Files for active category */}
                                <div style={{ maxHeight: 180, overflow: 'auto' }}>
                                    {filesByCategory(activeFileTab).length === 0 && (
                                        <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', padding: 8 }}>{fm('service.detail.filesEmpty', 'Nuk ka dokumente')}</div>
                                    )}
                                    {filesByCategory(activeFileTab).map(function(f: any) {
                                        return (
                                            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
                                                <a href={f.fileUrl} target="_blank" rel="noopener noreferrer"
                                                    style={{ flex: 1, fontSize: 12, color: '#1e3a5f', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as any }}>
                                                    {f.fileName || fm('service.detail.documentFallback', 'Dokument')}
                                                </a>
                                                <button onClick={function() { setDeleteFileConfirm({ id: f.id, name: f.fileName || fm('service.detail.documentFallback', 'Dokument') }); }}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2, flexShrink: 0 }}
                                                    onMouseEnter={function(e: any) { e.currentTarget.style.color = '#ef4444'; }}
                                                    onMouseLeave={function(e: any) { e.currentTarget.style.color = '#94a3b8'; }}>
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Delete file confirm */}
                        <MikaConfirmModal
                            isOpen={!!deleteFileConfirm}
                            onClose={function() { setDeleteFileConfirm(null); }}
                            onConfirm={handleFileDelete}
                            title={fm('service.detail.deleteFileTitle', 'Fshi dokumentin')}
                            message={fm('service.detail.deleteFileMessage', 'A jeni të sigurt që doni të fshini "{name}"?', { name: deleteFileConfirm?.name || '' })}
                            confirmLabel={fm('service.detail.deleteFileConfirm', 'Fshi')}
                            confirmColor="danger"
                        />

                        {/* Timeline */}
                        <div>
                            <div className="t-card-header">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                {fm('service.detail.timelineHeader', 'Aktiviteti ({count})', { count: (svc.events || []).length })}
                            </div>
                            <div className="t-card-body" style={{ padding: '10px 14px', maxHeight: 260, overflow: 'auto' }}>
                                {(svc.events || []).map(function(event: any, idx: number) {
                                    var dotColor = eventColors[event.eventType] || '#94a3b8';
                                    return (
                                        <div key={event.id} style={{ display: 'flex', gap: 10, paddingBottom: 10, marginBottom: 10, borderBottom: idx < (svc.events || []).length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, marginTop: 4, flexShrink: 0 }} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 12, color: '#0f172a', lineHeight: 1.4 }}>{formatEvent(event)}</div>
                                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{event.user?.name || fm('service.detail.timelineSystem', 'Sistemi')} — {fmtDate(event.createdAt)}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    currentServicing: state.service.currentServicing,
    permissions: state.app.permissions,
    loading: state.app.loading,
});

const mapDispatchToProps = (dispatch: any) => ({
    actions: bindActionCreators(serviceActions as any, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ServicingDetail) as any) as any;
